import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import pb from '../../lib/pocketbase';
import { Image, X, Save } from 'lucide-react';
import { FaTimes } from 'react-icons/fa'; // keeping for the image removal buttons
import styles from './ProjectForm.module.css';
import ConfirmationModal from './ConfirmationModal';

function ProjectForm({ project, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    project_date: '',
    description: '',
  });
  const [coverImage, setCoverImage] = useState(null); // { file: File | null, url: string | null, filename: string | null }
  const [galleryImages, setGalleryImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  const [dragOverCover, setDragOverCover] = useState(false);
  const [dragOverGallery, setDragOverGallery] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const allowedImageTypes = [
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/gif',
    'image/webp'
  ];
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.svg', '.gif', '.webp'];

  function hasValidImageTypeOrExt(file) {
    if (!file) return false;
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isValidType = allowedImageTypes.includes(file.type);
    const isValidExt = allowedExtensions.includes(ext);
    return isValidType || isValidExt;
  }

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        slug: project.slug || '',
        category: project.category || '',
        project_date: project.project_date ? project.project_date.split(' ')[0] : '',
        description: project.description || '',
      });

      if (project.cover_image) {
        const url = pb.files.getURL(project, project.cover_image);
        setCoverImage({ file: null, url: url, filename: project.cover_image });
      } else {
        setCoverImage(null);
      }

      if (project.gallery && project.gallery.length > 0) {
        const galleryUrls = project.gallery.map(filename => ({
          file: null, 
          url: pb.files.getURL(project, filename),
          filename: filename
        }));
        setGalleryImages(galleryUrls);
      } else {
        setGalleryImages([]);
      }

    } else {
      setFormData({ title: '', slug: '', category: '', project_date: '', description: '' });
      setCoverImage(null);
      setGalleryImages([]);
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!hasValidImageTypeOrExt(file)) {
        toast.error('Format d\'image non autorisé. Formats acceptés : jpeg, jpg, png, svg, gif, webp.');
        e.target.value = '';
        return;
      }
      
      const previewUrl = URL.createObjectURL(file);
      setCoverImage({ file: file, url: previewUrl, filename: file.name });
      if (formErrors.cover_image) {
        setFormErrors(prev => ({ ...prev, cover_image: null }));
      }
    }
  };

  const handleRemoveCoverImage = () => {
    setCoverImage(null);
  };

  const handleGalleryImagesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    const invalidFile = newFiles.find(f => !hasValidImageTypeOrExt(f));
    if (invalidFile) {
      toast.error(`Format non autorisé pour ${invalidFile.name}. Formats acceptés : jpeg, jpg, png, svg, gif, webp.`);
      e.target.value = '';
      return;
    }

    const newImages = newFiles.map(file => ({
      file: file,
      url: URL.createObjectURL(file),
      filename: file.name
    }));

    setGalleryImages(prevImages => [...prevImages, ...newImages]);
    if (formErrors.gallery) {
      setFormErrors(prev => ({ ...prev, gallery: null }));
    }
    e.target.value = '';
  };

  const handleRemoveGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(galleryImages[index]);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.parentNode);
    e.dataTransfer.setDragImage(e.target.parentNode, 20, 20);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const draggedOverItem = galleryImages[index];
    if (draggedItem === draggedOverItem) {
      return;
    }
    const items = galleryImages.filter(item => item !== draggedItem);
    items.splice(index, 0, draggedItem);
    setGalleryImages(items);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  const validateForm = () => {
    const errors = {};

    // Titre
    if (!formData.title.trim()) {
      errors.title = 'Le titre est obligatoire.';
    } else if (formData.title.trim().length < 3 || formData.title.trim().length > 100) {
      errors.title = 'Le titre doit contenir entre 3 et 100 caractères.';
    }

    // Slug
    if (!formData.slug.trim()) {
      errors.slug = 'Le slug est obligatoire.';
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.slug)) {
      errors.slug = 'Le slug doit être en format kebab-case (ex: mon-projet).';
    }

    // Description
    if (!formData.description.trim()) {
      errors.description = 'La description est obligatoire.';
    }

    // Image de couverture
    if (!coverImage) {
      errors.cover_image = 'L\'image de couverture est obligatoire.';
    }

    // Galerie
    if (galleryImages.length > 30) {
      errors.gallery = 'La galerie ne peut pas contenir plus de 30 images.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsConfirmOpen(true);
    }
  };

  const handleFinalSubmit = async () => {
    setIsConfirmOpen(false);
    setIsSubmitting(true);

    try {
      // Création ou mise à jour
      if (project) {
        // --- MISE À JOUR D'UN PROJET EXISTANT ---
        const fileData = new FormData();

        // 1. Gérer l'image de couverture
        const coverImageChanged = coverImage?.file || (!coverImage && project.cover_image);
        if (coverImageChanged) {
          fileData.append('cover_image', coverImage?.file || null);
        }

        // 2. Gérer la galerie
        const existingFilenames = galleryImages
          .filter(img => !img.file && img.filename) // Garder uniquement les fichiers existants
          .map(img => img.filename);

        const newFiles = galleryImages.filter(img => img.file); // Nouveaux fichiers à uploader

        // On envoie la liste des noms de fichiers existants pour que PocketBase sache lesquels conserver.
        // Les fichiers non listés seront supprimés.
        existingFilenames.forEach(filename => fileData.append('gallery', filename));

        // On ajoute les nouveaux fichiers à uploader.
        newFiles.forEach(img => fileData.append('gallery', img.file));

        // Si la galerie doit être vidée, on envoie `null`.
        if (galleryImages.length === 0) {
          fileData.append('gallery', null);
        }
        
        // 3. Mettre à jour les champs de texte et les fichiers
        const dataToUpdate = { ...formData };
        // On ajoute les champs de texte au FormData
        Object.keys(dataToUpdate).forEach(key => fileData.append(key, dataToUpdate[key]));

        const updatedRecord = await pb.collection('projects').update(project.id, fileData);
        onSave(updatedRecord);

      } else {
        // --- CRÉATION D'UN NOUVEAU PROJET ---

        if (!coverImage?.file) {
          toast.error('L\'image de couverture est obligatoire pour un nouveau projet.');
          return;
        }
        const data = new FormData();
        Object.keys(formData).forEach(key => data.append(key, formData[key]));
        data.append('cover_image', coverImage.file);
        galleryImages.forEach(img => data.append('gallery', img.file));

        // Log des données envoyées pour le débogage
        console.log("Données envoyées pour la création :");
        for (let [key, value] of data.entries()) {
            console.log(`${key}:`, value);
        }

        const newRecord = await pb.collection('projects').create(data);
        onSave(newRecord);
      }

    } catch (err) {
      console.error('Erreur détaillée lors de la sauvegarde :', err.response || err);

      if (err.response?.data) {
        const serverErrors = {};
        for (const [field, error] of Object.entries(err.response.data)) {
          serverErrors[field] = error.message;
        }
        setFormErrors(serverErrors);
        toast.error("Échec de la sauvegarde. Veuillez corriger les erreurs.");
      } else {
        toast.error(`Une erreur inattendue est survenue : ${err.message || 'Erreur inconnue.'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
          <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 700, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              
              <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                    <Image size={20} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{project ? 'Modifier le projet' : 'Nouveau Projet'}</h3>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Réalisation Site Web</p>
                  </div>
                </div>
                <button type="button" onClick={onCancel} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={20} />
                </button>
              </div>

              <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>
          {/* --- Champs de texte --- */}
          <div className={styles.formGroup}>
            <label htmlFor="title">Titre</label>
            <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} required />
            {formErrors.title && <div className={styles.fieldError}>{formErrors.title}</div>}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="slug">Slug</label>
            <input type="text" id="slug" name="slug" value={formData.slug} onChange={handleChange} required />
            {formErrors.slug && <div className={styles.fieldError}>{formErrors.slug}</div>}
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="category">Catégorie</label>
            <input type="text" id="category" name="category" value={formData.category} onChange={handleChange} />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="project_date">Date du projet</label>
            <input type="date" id="project_date" name="project_date" value={formData.project_date} onChange={handleChange} />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description" rows="6" value={formData.description} onChange={handleChange} required></textarea>
            {formErrors.description && <div className={styles.fieldError}>{formErrors.description}</div>}
          </div>

          {/* --- Image de couverture --- */}
          <div className={styles.formGroup}>
            <label>Image de couverture</label>
            {formErrors.cover_image && <div className={styles.fieldError}>{formErrors.cover_image}</div>}
            <div 
              className={`${styles.uploadBox} ${dragOverCover ? styles['drag-over'] : ''}`}
              onClick={() => document.getElementById('cover-image-upload').click()}
              onDragOver={(e) => { e.preventDefault(); setDragOverCover(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOverCover(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverCover(false);
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  handleCoverImageChange({ target: { files: e.dataTransfer.files } });
                }
              }}
            >
              <input
                type="file"
                id="cover-image-upload"
                onChange={handleCoverImageChange}
                accept=".jpeg,.jpg,.png,.svg,.gif,.webp"
                style={{ display: 'none' }}
              />
              {coverImage?.url ? (
                <>
                  <img src={coverImage.url} alt="Aperçu de la couverture" className={styles.imagePreview} />
                  <div className={styles.editOverlay}>
                    <span>🖼️ Changer l'image</span>
                  </div>
                  <button 
                    type="button" 
                    onClick={(e) => { e.stopPropagation(); handleRemoveCoverImage(); }}
                    className={styles.removeImageBtn}
                  >
                    <FaTimes />
                  </button>
                </>
              ) : (
                <p>
                  <span className={styles.uploadIcon}>🖼️</span>
                  Cliquez ou glissez-déposez une image ici
                  <br />
                  <small>Format recommandé : 1200x800px</small>
                </p>
              )}
            </div>
          </div>

          {/* --- Galerie d'images --- */}
          <div className={styles.formGroup}>
            <label>Galerie d'images</label>
            {formErrors.gallery && <div className={styles.fieldError}>{formErrors.gallery}</div>}
            <div 
              className={`${styles.uploadBox} ${dragOverGallery ? styles['drag-over'] : ''}`}
              onClick={() => document.getElementById('gallery-upload').click()}
              onDragOver={(e) => { e.preventDefault(); setDragOverGallery(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOverGallery(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverGallery(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  handleGalleryImagesChange({ target: { files: e.dataTransfer.files } });
                }
              }}
            >
              <input
                type="file"
                id="gallery-upload"
                onChange={handleGalleryImagesChange}
                accept=".jpeg,.jpg,.png,.svg,.gif,.webp"
                style={{ display: 'none' }}
                multiple
              />
              <p>
                <span className={styles.uploadIcon}>➕</span>
                Ajouter des images à la galerie
              </p>
              <small className={styles.formHint}>Formats autorisés : JPG, PNG, SVG, GIF, WEBP.</small>
              {galleryImages.length > 0 && (
                  <small className={styles.formHint}>Astuce : glissez-déposez les images pour les réordonner.</small>
              )}
            </div>
            {galleryImages.length > 0 && (
              <div className={styles.galleryGrid} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
                {galleryImages.map((img, idx) => (
                  <div 
                    key={img.filename || `new-${idx}`}
                    className={`${styles.galleryItem}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, idx)}
                  >
                    <img src={img.url} alt={`Galerie ${idx + 1}`} />
                    <button 
                      type="button" 
                      onClick={(e) => { e.stopPropagation(); handleRemoveGalleryImage(idx); }}
                      className={styles.removeImageBtn}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

              </div>

              <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div />
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" onClick={onCancel} disabled={isSubmitting} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={isSubmitting || isConfirmOpen} style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                    <Save size={18} /> {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder le projet'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleFinalSubmit}
        title={project ? 'Confirmer la modification' : 'Confirmer la création'}
      >
        Êtes-vous sûr de vouloir enregistrer ce projet ?
      </ConfirmationModal>
    </>
  );
}

export default ProjectForm;
